import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { compile } from '../../../utils/search'

interface Props {
  teamIdList: number[]
  callbackTeams: (teams: { id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]) => void
  teamList: { id: number, origin_name: string, name: string, members: { id: number, name: string }[] }[]
  memberIdList: number[]
  callbackMembers: (teams: { id: number, name: string }[]) => void
  memberList: { id: number, name: string }[]
  isClear?: boolean
}

const AddTeamAndMemberModule: FC<Props> = (props) => {
  const {
    teamIdList,
    callbackTeams,
    teamList,
    memberIdList,
    callbackMembers,
    memberList,
    isClear,
  } = props

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [selectTeams, setSelectTeams] = useState<{id: number, origin_name: string, name: string, members: { id: number, name: string }[]}[]>([])
  const [teams, setTeams] = useState<{id: number, origin_name: string, name: string, members: { id: number, name: string }[]}[]>([])

  const [selectMembers, setSelectMembers] = useState<{id: number, name: string}[]>([])
  const [members, setMembers] = useState<{id: number, name: string}[]>([])

  const [searchWord, setSearchWord] = useState<string>("")

  const membersInit = useMemo(() => (
    memberList || []
  ), [memberList])

  const teamsInit = useMemo(() => (
    teamList || []
  ), [teamList])

  const getUniqueValues = ([...array]) => (
    array.filter((value, _index, self) => self.indexOf(value) === self.lastIndexOf(value))
  )

  const clickTeam = (team_id: number) => {
    if (selectTeams.map((v) => v.id).includes(team_id)) {
      setSelectTeams(selectTeams.filter((v) => v.id !== team_id))
    } else {
      const targetTeam = teams.find((v) => v.id === team_id)
      setSelectTeams(
        [
          ...selectTeams,
          { id: team_id, origin_name: targetTeam?.origin_name || "", name: targetTeam?.name || "", members: targetTeam?.members || [] },
        ],
      )
    }
  }

  const clickMember = (member_id: number) => {
    if (selectMembers.map((v) => v.id).includes(member_id)) {
      setSelectMembers(selectMembers.filter((v) => v.id !== member_id))
    } else {
      setSelectMembers([...selectMembers, { id: member_id, name: members.find((v) => v.id === member_id)?.name || "" }])
    }
  }

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setTeams([...teamsInit])
      setMembers([...membersInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const tmpTeamList = teamsInit.filter((team) => {
      if (team === null) return false
      return (compile(word).test(team.name.toLowerCase())) || selectTeams.map((v) => v.id).includes(team.id)
    })
    setTeams(sortBy(tmpTeamList, 'id'))
    const tmpMemberList = membersInit.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name.toLowerCase())) || selectMembers.map((v) => v.id).includes(member.id)
    })
    setMembers(sortBy(tmpMemberList, 'id'))
  }

  const reset = () => {
    setSearchWord("")
    setTeams([...teamsInit])
    setMembers([...membersInit])
    setSelectTeams([])
    setSelectMembers([])
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    if (getUniqueValues([...selectTeams.map((v) => v.id), ...teamIdList]).length) {
      callbackTeams(selectTeams)
    }
    if (getUniqueValues([...selectMembers.map((v) => v.id), ...memberIdList]).length) {
      callbackMembers(selectMembers)
    }
  }

  useEffect(() => {
    setTeams(teamList || [])
  }, [teamList])

  useEffect(() => {
    if (!teamList) return
    setSelectTeams(teamList.filter((v) => teamIdList.includes(v.id)))
  }, [teamIdList, teamList])

  useEffect(() => {
    setMembers(memberList || [])
  }, [memberList])

  useEffect(() => {
    if (!memberList) return
    setSelectMembers(memberList.filter((v) => memberIdList.includes(v.id)))
  }, [memberIdList, memberList])

  useEffect(() => {
    if (!isClear) return
    setSelectTeams([])
    setSelectMembers([])
  }, [isClear])

  return (
    <>
      <div
        className="add_button"
        onClick={() => openSearch()}
        role="none"
      >
        <FontAwesomeIcon icon={['fas', 'plus']} />
        &nbsp;
        追加
        {
          isSearchDisplay && (
            <div className={`search_box ${isSearchDisplay || 'none'}`}>
              <div className="search_detail">
                <div className="category">
                  <div className="category_title">
                    チーム・メンバー
                    {
                      (selectTeams.length || selectMembers.length) ? (
                        <div
                          className="select_reset"
                          role="none"
                          onClick={reset}
                        >
                          リセット
                        </div>
                      ) : (
                        <></>
                      )
                    }
                  </div>
                  <div className="search_area">
                    <FontAwesomeIcon
                      icon={['fas', 'search']}
                      className="icon"
                    />
                    <input
                      type="text"
                      placeholder="チーム・メンバーを検索"
                      onChange={(event) => search(event.target.value)}
                      value={searchWord}
                    />
                    {
                      searchWord && (
                        <button type="button" className="reset" onClick={() => search("")}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      )
                    }
                  </div>
                  {
                    !teams.length && !members.length && (
                      <>
                        選択できるチーム・メンバーがいません
                      </>
                    )
                  }
                  {
                    teams.length ? (
                      <>
                        <div className="category_title">
                          チーム
                        </div>
                        <div className="chip_list">
                          {
                            teams.map((team) => (
                              <button
                                type="button"
                                className={`chip ${selectTeams.map((v) => v.id).includes(team.id) ? "checked" : ""}`}
                                key={`team_${team.id}`}
                                onClick={() => clickTeam(team.id)}
                              >
                                {team.name}
                              </button>
                            ))
                          }
                        </div>
                      </>
                    ) : (
                      <></>
                    )
                  }
                  {
                    members.length ? (
                      <>
                        <div className="category_title">
                          メンバー
                        </div>
                        <div className="chip_list">
                          {
                            members.map((member) => (
                              <button
                                type="button"
                                className={`chip ${selectMembers.map((v) => v.id).includes(member.id) ? "checked" : ""}`}
                                key={`member_${member.id}`}
                                onClick={() => clickMember(member.id)}
                              >
                                {member.name}
                              </button>
                            ))
                          }
                        </div>
                      </>
                    ) : (
                      <></>
                    )
                  }
                </div>
              </div>
            </div>
          )
        }
      </div>
      <div className={`close_screen ${isSearchDisplay || 'none'}`} role="presentation" onClick={() => closeSearch()} />
    </>
  )
}

AddTeamAndMemberModule.defaultProps = {
  isClear: false,
}

export default AddTeamAndMemberModule
